/**
 * 催续住催退
 */
import React, { useImperativeHandle, useState } from 'react';
import { Drawer, Spin, Table, Button, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { CheckOutlined } from '@ant-design/icons';
import cls from 'classnames';

import { getOrderUrgeList, pushUrgeMessage } from '@/api/order';
import DateJS, { format2FullTimeStr } from '@/utils/date';
import getRandomId from '@/utils/uuid';
import { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import ContentBlock from '@/components/Reservation/ContentBlock';
import useConfirmPortal from '@/components/PortalConfirm';
import { renderIntlLabel } from '@/utils';

import styles from './index.module.scss';

enum ComputerStatus {
  Shutdown = 0, // 0关机
  Working = 1, // 1使用中
  Hibernate = 2, // 2开机未使用
}

const ComputerStatusMap = {
  [ComputerStatus.Shutdown]: renderIntlLabel('roomstate_realtime_list_hint_computer_off'),
  [ComputerStatus.Working]: renderIntlLabel('device_using'),
  [ComputerStatus.Hibernate]: renderIntlLabel('device_open_not_use'),
};

const StatusNameMap = {
  [ComputerStatus.Shutdown]: styles.statusShutdown,
  [ComputerStatus.Working]: styles.statusWorking,
  [ComputerStatus.Hibernate]: styles.statusHibernate,
};

interface IRecord {
  customerName: string; //	使用人
  displayName: string; //	机器展示名
  lastUrgeTime: string; // 上次发送时间
  no: number; // 机器编号
  recNo: string; //	接待单编号
  resvNo: string; //	主单编号
  roomId: number; //	房间id
  roomNo: string; //	房号
  useStatus: ComputerStatus; //	使用状态【0关机 1使用中 2开机未使用】
  wyuid: string; //	网鱼ID
  indexId: string;
  checkable: boolean; // 是否可以选择
}

interface MessageInfo {
  code: string; // 消息模板编号
  content: string; // 消息内容
}

// 续住明细
const Index = (props) => {
  const [modalVisible, setVisible] = useState<boolean>(false);
  const [isLoading, setLoadingStatus] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    messageCode: null,
    selectList: [],
  });
  const [detailData, setDetailData] = useState<{
    messageList: MessageInfo[];
    deviceList: IRecord[];
  }>({
    messageList: [],
    deviceList: [],
  });
  const confirmPortal = useConfirmPortal();

  const { messageCode, selectList } = formData;
  const { messageList, deviceList } = detailData;

  const closeModal = () => {
    setVisible(false);
  };

  // 获取详细信息
  const getDetailData = async (recList) => {
    try {
      setLoadingStatus(true);
      const res = await getOrderUrgeList({ recNos: recList });
      const { currentTime, urgeMsgList, urgeRoomList } = res.data;
      const currentDate = DateJS(currentTime);
      const defaultSelectList = [];
      setDetailData({
        messageList: urgeMsgList,
        deviceList: urgeRoomList.map((item: IRecord) => {
          const { lastUrgeTime, useStatus, customerName } = item;
          // 这个电脑必须有人在用，且不是管理员
          // 这个电脑必须开机在
          // 之前没提醒 或 上次发送时间至今已经超过了10分钟
          const checkable =
            customerName !== '管理员' &&
            useStatus === ComputerStatus.Working &&
            (!lastUrgeTime || currentDate.diff(lastUrgeTime, 'minute') > 10);
          const itemId = getRandomId();
          if (checkable) {
            defaultSelectList.push(itemId);
          }
          return {
            ...item,
            indexId: itemId,
            checkable,
          };
        }),
      });
      // 默认选择第一个消息 所有可选电脑
      setFormData({
        messageCode: urgeMsgList[0].code,
        selectList: defaultSelectList,
      });
      setLoadingStatus(false);
    } catch (e) {
      //
      closeModal();
    }
  };

  // 提交数据
  const handleSubmitClick = async () => {
    if (selectList.length) {
      const selectIdMap = {};
      selectList.forEach((itemId) => {
        selectIdMap[itemId] = true;
      });
      const urgeItemList = [];
      deviceList.forEach((item) => {
        if (selectIdMap[item.indexId] && item.checkable) {
          urgeItemList.push({
            no: item.no,
            recNo: item.recNo,
            resvNo: item.resvNo,
            roomId: item.roomId,
            wyuid: item.wyuid,
          });
        }
      });
      const targetMessage = messageList.find((item) => item.code === messageCode);
      const deviceCount = urgeItemList.length;
      if (deviceCount && targetMessage) {
        confirmPortal.showDrawer({
          title: renderIntlLabel('order_action_confirm_send_msg'),
          content: (
            <div className={styles.pushConfirmContent}>
              <div>
                {renderIntlLabel('order_title_send_msg_some_clients', { count: deviceCount })}
              </div>
              <div className={styles.pushMessageText}>{targetMessage.content}</div>
            </div>
          ),
          cancelText: renderIntlLabel('order_frontDesk_action_btn_cancel'),
          onCancel: () => {
            confirmPortal.closeDrawer();
          },
          confirmText: renderIntlLabel('order_action_send_msg_immediately'),
          onConfirm: async () => {
            confirmPortal.closeDrawer();
            setLoadingStatus(true);
            try {
              await pushUrgeMessage({ msgCode: messageCode, urgeItems: urgeItemList });
              closeModal();
              message.success(renderIntlLabel('order_action_send_successfully'));
            } catch (e) {
              //
            }
            setLoadingStatus(false);
          },
        });
      }
    }
  };

  // 选择框属性
  const getCheckboxProps = (item: IRecord) => {
    return {
      disabled: !item.checkable,
    };
  };

  // 修改选中数据
  const handleSelectChange = (value) => {
    setFormData({ ...formData, selectList: value });
  };

  // 修改消息类型
  const handleMessageChange = (value) => {
    setFormData({
      ...formData,
      messageCode: value,
    });
  };

  const renderMessageList = () => {
    return (
      <div className={styles.messageList}>
        {messageList.map((item) => {
          return (
            <div
              className={cls(
                styles.messageItem,
                item.code === messageCode ? styles.activeMessageItem : null,
              )}
              onClick={() => handleMessageChange(item.code)}
            >
              <div>{item.content}</div>
              <CheckOutlined className={styles.messageCheckIcon} />
            </div>
          );
        })}
      </div>
    );
  };

  const renderDeviceStatus = (val) => {
    return (
      <div className={styles.deviceStatusWrapper}>
        <i className={cls(styles.statusIndicator, StatusNameMap[val])} />
        <span>{ComputerStatusMap[val]}</span>
      </div>
    );
  };

  useImperativeHandle(
    props.innerRef,
    () => ({
      openModal: (recList) => {
        setFormData({
          messageCode: null,
          selectList: [],
        });
        setDetailData({
          messageList: [],
          deviceList: [],
        });
        if (recList && recList.length) {
          getDetailData(recList);
          setVisible(true); // 显示弹框
        }
      },
    }),
    [],
  );

  const columns: ColumnsType<IRecord> = [
    { title: renderIntlLabel('order_roomorder_room'), dataIndex: 'roomNo' },
    { title: renderIntlLabel('order_selfService_detail_computerNo'), dataIndex: 'no' },
    {
      title: renderIntlLabel('order_selfService_detail_computerDisplayName'),
      dataIndex: 'displayName',
    },
    {
      title: renderIntlLabel('order_title_status_of_computer_use'),
      dataIndex: 'useStatus',
      render: renderDeviceStatus,
    },
    { title: renderIntlLabel('order_title_person_of_computer_use'), dataIndex: 'customerName' },
    {
      title: renderIntlLabel('order_title_last_send_time'),
      dataIndex: 'lastUrgeTime',
      render: (val) => {
        return val ? format2FullTimeStr(val) : null;
      },
    },
  ];

  return (
    <Drawer
      closable={false}
      destroyOnClose
      className={styles.drawerBodyWrapper}
      visible={modalVisible}
    >
      <Spin spinning={isLoading}>
        <DrawerHeader
          title={renderIntlLabel('order_action_urge_extend_return')}
          onClose={closeModal}
        />
        <div className={styles.drawerContent}>
          <ContentBlock title={renderIntlLabel('order_title_content_of_msg')}>
            {renderMessageList()}
          </ContentBlock>
          <ContentBlock title={renderIntlLabel('order_title_list_of_computers')}>
            <Table
              rowKey="indexId"
              scroll={{ x: 'max-content' }}
              columns={columns}
              dataSource={deviceList}
              rowSelection={{
                fixed: true,
                selectedRowKeys: selectList,
                onChange: handleSelectChange,
                getCheckboxProps,
              }}
              pagination={false}
            />
          </ContentBlock>
        </div>
        <DrawerFooter>
          <Button type="primary" onClick={handleSubmitClick}>
            {renderIntlLabel('order_action_send')}
          </Button>
          <Button onClick={closeModal}>{renderIntlLabel('order_action_cancel')}</Button>
        </DrawerFooter>
      </Spin>
    </Drawer>
  );
};

export default Index;
